import { useEffect, useMemo, useState } from "react";
import Icon from "../icons/Icon";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import { useRoles } from "../../hooks/useRoles";
import { PREDEFINED_ROLES, ROLE_LIMITS } from "../../config/roles";
import { countAccessLevels } from "../../config/matrix";
import DeleteRoleModal from "./DeleteRoleModal";
import "../../styles/components/settings-pages.css";
import "../../styles/components/roles-page.css";

const FILTER_OPTIONS = [
  { value: "all", label: "All roles" },
  { value: "predefined", label: "Predefined only" },
  { value: "custom", label: "Custom only" },
];

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function matchesQuery(role, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    (role.name || "").toLowerCase().includes(q) ||
    (role.description || "").toLowerCase().includes(q)
  );
}

const RolesPage = () => {
  const { company, hasPermission } = useAuth();
  const navigate = useNavigate();
  const {
    roles: customRoles,
    loading: rolesLoading,
    error: rolesError,
    removeOptimistically,
  } = useRoles(company?.id);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [roleToDelete, setRoleToDelete] = useState(null);

  const canManage = hasPermission("set:roles:edit");
  const rolesPath = `/company/${company?.slug || ""}/settings/roles`;

  // Subscribe to users for assignment counts (PRD §6.5).
  // Defensive guard: route gating on /settings/roles already keeps
  // unauthorized users out, but mirroring it at the data layer prevents
  // a future rule tightening (or a bug in route gating) from corrupting
  // the Firestore SDK state machine via permission-denied onSnapshot.
  useEffect(() => {
    if (!company?.id) return undefined;
    if (
      !hasPermission("set:roles:edit") &&
      !hasPermission("set:roles:view")
    ) {
      setUsersLoading(false);
      return undefined;
    }
    const unsub = onSnapshot(
      collection(db, "companies", company.id, "users"),
      (snap) => {
        setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setUsersLoading(false);
      },
      (err) => {
        console.error("Users snapshot failed:", err);
        setUsersLoading(false);
      },
    );
    return unsub;
  }, [company?.id, hasPermission]);

  const predefinedRows = useMemo(() => {
    return Object.entries(PREDEFINED_ROLES).map(([key, def]) => ({
      id: `predefined:${key}`,
      key,
      type: "predefined",
      name: def.label,
      description: def.description,
      permissions: def.permissions,
      accessLevels: countAccessLevels(def.permissions),
      assignedCount: users.filter(
        (u) =>
          u.role === key &&
          (u.status === "active" || u.status === "pending"),
      ).length,
    }));
  }, [users]);

  const customRows = useMemo(() => {
    return customRoles.map((role) => {
      const perms = role.permissions || [];
      return {
        id: role.id,
        type: "custom",
        name: role.name || "(unnamed)",
        description: role.description || "",
        permissions: perms,
        accessLevels: countAccessLevels(perms),
        raw: role,
        assignedCount: users.filter(
          (u) =>
            u.role === "custom" &&
            u.customRoleId === role.id &&
            (u.status === "active" || u.status === "pending"),
        ).length,
      };
    });
  }, [customRoles, users]);

  const filteredPredefined = useMemo(() => {
    if (typeFilter === "custom") return [];
    return predefinedRows.filter((r) => matchesQuery(r, searchQuery));
  }, [predefinedRows, searchQuery, typeFilter]);

  const filteredCustom = useMemo(() => {
    if (typeFilter === "predefined") return [];
    return customRows.filter((r) => matchesQuery(r, searchQuery));
  }, [customRows, searchQuery, typeFilter]);

  const customCount = customRows.length;
  const customLimit = ROLE_LIMITS.MAX_CUSTOM_ROLES_PER_COMPANY;
  const atMax = customCount >= customLimit;

  const handleCreate = () => {
    if (atMax) return;
    navigate(`${rolesPath}/new`);
  };

  const handleEdit = (row) => {
    navigate(`${rolesPath}/${row.id}/edit`);
  };

  const handleDeleteRequest = (row) => {
    setRoleToDelete(row.raw);
  };

  const loading = rolesLoading || usersLoading;

  return (
    <div className="roles-page">
      <div className="roles-page-header">
        <div>
          <h1 className="settings-page-title">Roles</h1>
          <p className="settings-page-subtitle">
            Define what team members can see and do.
          </p>
        </div>
      </div>

      <div className="roles-controls">
        <input
          type="search"
          className="roles-search"
          placeholder="Search by name or description"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className="roles-filter"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <div className="roles-controls-spacer" />
        {canManage && (
          <button
            type="button"
            className="settings-btn settings-btn-primary"
            onClick={handleCreate}
            disabled={atMax}
            title={atMax ? `Maximum of ${customLimit} custom roles reached` : ""}
          >
            + Create Custom Role
          </button>
        )}
      </div>

      {atMax && (
        <div className="roles-banner roles-banner-warn">
          You've reached the maximum of {customLimit} custom roles. Delete one
          before creating another.
        </div>
      )}

      {rolesError && (
        <div className="roles-banner roles-banner-error">
          Failed to load custom roles. Refresh the page to try again.
        </div>
      )}

      {loading ? (
        <div className="roles-loading">
          <div className="spinner" />
          <p>Loading roles…</p>
        </div>
      ) : (
        <div className="roles-card">
          {filteredPredefined.length > 0 && (
            <div className="roles-section">
              <div className="roles-section-header">Predefined</div>
              {filteredPredefined.map((row) => (
                <RoleRow
                  key={row.id}
                  row={row}
                  canManage={false}
                  onEdit={handleEdit}
                  onDelete={handleDeleteRequest}
                />
              ))}
            </div>
          )}

          {filteredCustom.length > 0 && (
            <div className="roles-section">
              <div className="roles-section-header">
                Custom <span className="roles-section-count">
                  {customCount} of {customLimit}
                </span>
              </div>
              {filteredCustom.map((row) => (
                <RoleRow
                  key={row.id}
                  row={row}
                  canManage={canManage}
                  onEdit={handleEdit}
                  onDelete={handleDeleteRequest}
                />
              ))}
            </div>
          )}

          {filteredCustom.length === 0 &&
            filteredPredefined.length === 0 &&
            !searchQuery &&
            customRows.length === 0 && (
              <div className="roles-empty">
                <div className="roles-empty-icon" aria-hidden="true">
                  <Icon name="user-cog" size={22} />
                </div>
                <h3>No custom roles yet</h3>
                <p>
                  Create one to give team members access tailored to their
                  responsibilities.
                </p>
                {canManage && (
                  <button
                    type="button"
                    className="settings-btn settings-btn-primary"
                    onClick={handleCreate}
                  >
                    + Create your first custom role
                  </button>
                )}
              </div>
            )}

          {filteredCustom.length === 0 &&
            filteredPredefined.length === 0 &&
            (searchQuery || typeFilter !== "all") && (
              <div className="roles-empty roles-empty-search">
                No roles match your search.
              </div>
            )}
        </div>
      )}

      {roleToDelete && (
        <DeleteRoleModal
          role={roleToDelete}
          onClose={() => setRoleToDelete(null)}
          onDeleted={(deletedId) => {
            // Optimistic removal — the Firestore SDK can take a few seconds
            // to fire the post-delete snapshot when its bloom filter triggers
            // a recovery re-query. The next snapshot is still authoritative.
            removeOptimistically(deletedId);
            setRoleToDelete(null);
          }}
        />
      )}
    </div>
  );
};

const RoleRow = ({ row, canManage, onEdit, onDelete }) => {
  const isCustom = row.type === "custom";
  return (
    <div className="role-row">
      <div className="role-row-icon" aria-hidden="true">
        {getInitials(row.name)}
      </div>
      <div className="role-row-main">
        <div className="role-row-name" title={row.name}>
          {row.name}
        </div>
        <div className="role-row-meta">
          {row.description ? (
            <span className="role-row-desc" title={row.description}>
              {row.description}
            </span>
          ) : (
            <span className="role-row-desc role-row-desc-empty">
              No description
            </span>
          )}
          <span className="role-row-meta-sep">·</span>
          <span>
            {row.accessLevels} access level{row.accessLevels === 1 ? "" : "s"}
          </span>
          <span className="role-row-meta-sep">·</span>
          <span>
            {row.assignedCount} user{row.assignedCount === 1 ? "" : "s"}
          </span>
        </div>
      </div>
      <div className={`role-row-badge role-row-badge-${row.type}`}>
        {isCustom ? "Custom" : "Predefined"}
      </div>
      <div className="role-row-actions">
        {isCustom && canManage ? (
          <>
            <button
              type="button"
              className="settings-btn settings-btn-secondary settings-btn-sm"
              onClick={() => onEdit(row)}
            >
              Edit
            </button>
            <button
              type="button"
              className="settings-btn settings-btn-secondary settings-btn-sm role-row-delete"
              onClick={() => onDelete(row)}
              disabled={row.assignedCount > 0}
              title={
                row.assignedCount > 0
                  ? `Assigned to ${row.assignedCount} user${row.assignedCount === 1 ? "" : "s"}. Reassign them via the Team page first.`
                  : "Delete role"
              }
            >
              Delete
            </button>
          </>
        ) : (
          <span className="role-row-readonly">Read-only</span>
        )}
      </div>
    </div>
  );
};

export default RolesPage;
